/**
 * SelectActionsPuertoRicoPhase - アクション選択フェーズUIコンポーネント（Puerto Rico専用）
 *
 * Puerto Rico固有ルール:
 * - 2種類のアクションのみ: ENGINEER, LOCOMOTIVE
 * - 選択は任意（スキップ可能）
 * - 選択する場合は $5 を支払う
 * - アクションマーカーシステムは不使用
 *
 * @see docs/specifications/logic/puerto_rico/phase_03_select_actions_override.md
 */

import type { PuertoRicoActionType } from '../../types';
import { useTranslations } from '../../i18n';

interface SelectActionsPuertoRicoPhaseProps {
  /** プレイヤーの現金 */
  playerCash: number;
  /** 現在の機関車レベル */
  locomotiveLevel: number;
  /** 自分のターンかどうか */
  isMyTurn: boolean;
  /** アクション選択コールバック */
  onSelectAction: (action: PuertoRicoActionType) => void;
  /** スキップコールバック */
  onSkip: () => void;
}

/**
 * Phase 3: アクション選択フェーズUI（Puerto Rico専用）
 */
export function SelectActionsPuertoRicoPhase({
  playerCash,
  locomotiveLevel,
  isMyTurn,
  onSelectAction,
  onSkip,
}: SelectActionsPuertoRicoPhaseProps) {
  const t = useTranslations();

  // アクション選択コスト
  const ACTION_COST = 5;
  const MAX_LOCO_LEVEL = 6;

  // 資金不足チェック
  const canAffordAction = playerCash >= ACTION_COST;
  // 機関車レベル上限チェック
  const canSelectLocomotive = locomotiveLevel < MAX_LOCO_LEVEL;

  // Puerto Ricoのアクション定義
  const PUERTO_RICO_ACTIONS: {
    type: PuertoRicoActionType;
    name: string;
    description: string;
    disabled: boolean;
    disabledReason?: string;
  }[] = [
    {
      type: 'ENGINEER',
      name: t.selectActions.actions.engineer.name,
      description: t.selectActions.actions.engineer.description,
      disabled: !canAffordAction,
      disabledReason: !canAffordAction ? t.selectActions.insufficientFunds : undefined,
    },
    {
      type: 'LOCOMOTIVE',
      name: t.selectActions.actions.locomotive.name,
      description: `${t.selectActions.actions.locomotive.description}`,
      disabled: !canAffordAction || !canSelectLocomotive,
      disabledReason: !canAffordAction
        ? t.selectActions.insufficientFunds
        : !canSelectLocomotive
        ? t.selectActions.maxLevel
        : undefined,
    },
  ];

  return (
    <div className="space-y-4">
      {!isMyTurn ? (
        <p className="text-muted-foreground text-sm">{t.common.waiting}</p>
      ) : (
        <>
          {/* アクション選択ボタン */}
          <div className="grid grid-cols-2 gap-1.5">
            {PUERTO_RICO_ACTIONS.map((action) => (
              <button
                key={action.type}
                className="action-card text-left p-3"
                onClick={() => !action.disabled && onSelectAction(action.type)}
                disabled={action.disabled}
                data-action={action.type}
                data-disabled={action.disabled}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-heading text-sm font-semibold text-foreground leading-tight">
                      {action.name}
                    </div>
                    <div className="text-[10px] text-muted-foreground leading-tight mt-1">
                      {action.description}
                    </div>
                  </div>
                  <div className="text-sm font-bold text-warning">
                    ${ACTION_COST}
                  </div>
                </div>
                {action.disabledReason && (
                  <div className="text-[10px] text-destructive mt-1">
                    {action.disabledReason}
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* スキップボタン */}
          <button
            className="btn-secondary w-full py-2"
            onClick={onSkip}
          >
            {t.selectActions.puertoRico?.skip || 'Skip (No Cost)'}
          </button>

          {/* ヘルプテキスト */}
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer hover:text-foreground">
              {t.selectActions.helpTitle}
            </summary>
            <ul className="mt-2 ml-4 list-disc space-y-1">
              <li>{t.selectActions.puertoRico?.helpOptional || 'Action selection is optional'}</li>
              <li>{t.selectActions.puertoRico?.helpCost || `Selecting an action costs $${ACTION_COST}`}</li>
            </ul>
          </details>
        </>
      )}
    </div>
  );
}

export default SelectActionsPuertoRicoPhase;
